import React, { memo, useMemo, useState } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import { OPERATOR_IN, creators, print } from '@atlaskit/jql-ast';
import { Box, Stack, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import ListCellWithPopup, {
	TagItem,
	TagItemPopupContentWrapper,
} from '@atlassian/jira-list-with-popup';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	INLINE_DIALOG,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { issueLinks_nativeIssueTable_IssueLinksCell$key } from '@atlassian/jira-relay/src/__generated__/issueLinks_nativeIssueTable_IssueLinksCell.graphql';
import { Link } from '@atlassian/jira-router';
import ErrorCell from '../../error-cell';
import messages from './messages';

export type Props = {
	fieldRef: issueLinks_nativeIssueTable_IssueLinksCell$key;
	issueSearchBaseUrl: string;
	issueKey: string;
	maxDisplayLimit?: number;
	/**
	 * Whether the ListCellWithPopup should be open by default
	 * Default = false
	 */
	initialIsOpen?: boolean;
	/**
	 * Whether the accordions should be open by default when opening the ListCellWithPopup
	 * Default = false
	 */
	accordionInitialIsOpen?: boolean;
};

export type IssueLink = {
	key: string;
	name: string;
	href: string;
	relationName?: string | null;
	shouldDisplayStrikethrough: boolean;
};

type Issue = {
	key: string;
	name: string;
	href: string;
	shouldDisplayStrikethrough: boolean;
};

type Relationship = {
	key: string;
	issues: Issue[];
};

/**
 * Expected JQL Output: `issue in linkedIssues(`${issueKey})`
 */
const getLinkJql = (issueKey: string) => {
	const jast = creators.jast(
		creators.query(
			creators.terminalClause(
				creators.field('issue'),
				creators.operator(OPERATOR_IN),
				creators.functionOperand(creators.functionString('linkedIssues'), [
					creators.argument(issueKey),
				]),
			),
		),
	);

	return print(jast, {
		operatorCase: 'upper',
	});
};

/**
 * @param issueLinks - An object containing issue information, and the relationship name
 * @returns An array that groups issueLinks by relationship. A relationship could be "blocks", "is blocked by", "relates to", etc.
 */
export const groupByRelationship = (issueLinks: IssueLink[]): Relationship[] => {
	const relationshipGroupings: { [key: string]: Relationship } = {};

	issueLinks.forEach(({ key, name, href, relationName, shouldDisplayStrikethrough }) => {
		if (relationName) {
			if (!relationshipGroupings[relationName]) {
				relationshipGroupings[relationName] = {
					key: relationName,
					issues: [],
				};
			}

			relationshipGroupings[relationName].issues.push({
				key,
				name,
				href,
				shouldDisplayStrikethrough,
			});
		}
	});

	return Object.values(relationshipGroupings);
};

/**
 *
 * @param links - An array of issueLinks grouped by relationship
 * The links array is sorted alphabetically by relationship. Inside each issues list, the issues are also sorted alphabetically by name
 */
export const sortRelationshipsAndTheirIssues = (relationships: Relationship[]): void => {
	relationships.forEach((relationship) =>
		relationship.issues.sort((a, b) => a.name.localeCompare(b.name)),
	);
	relationships.sort((a, b) => a.key.localeCompare(b.key));
};

export const IssueLinksCell = memo<Props>(
	({
		fieldRef,
		issueSearchBaseUrl,
		issueKey,
		initialIsOpen = false,
		accordionInitialIsOpen = false,
	}: Props) => {
		const { formatMessage } = useIntl();

		// the first:1000 is max that frontend is allowed to request. BE may override this upper limit and return lesser items.
		const data = useFragment<issueLinks_nativeIssueTable_IssueLinksCell$key>(
			graphql`
				fragment issueLinks_nativeIssueTable_IssueLinksCell on JiraIssueLinkField {
					issueLinkConnection(first: 1000) {
						pageInfo {
							hasNextPage
						}
						edges {
							node {
								issue {
									webUrl
									key
									fieldsById(ids: ["statusCategory"]) {
										edges {
											node {
												... on JiraStatusCategoryField {
													statusCategory {
														statusCategoryId
													}
												}
											}
										}
									}
								}
								relationName
							}
						}
					}
				}
			`,
			fieldRef,
		);

		const issueLinks = data?.issueLinkConnection?.edges;

		const filteredIssueLinks: IssueLink[] = useMemo(
			() =>
				issueLinks
					?.map((edge) =>
						edge?.node?.issue?.key != null
							? {
									key: edge.node.issue?.key || 'missingId',
									name: edge.node.issue?.key,
									href: edge.node.issue?.webUrl || '',
									relationName: edge.node.relationName,
									shouldDisplayStrikethrough:
										edge.node.issue?.fieldsById?.edges?.[0]?.node?.statusCategory
											?.statusCategoryId === '3' || false,
								}
							: null,
					)
					.filter(Boolean) ?? [],
			[issueLinks],
		);

		// JIS doesn't return exact total count of issueLinks, but we do know whether there are unfetched items
		const maxIssueLinksReached = data?.issueLinkConnection?.pageInfo?.hasNextPage === true || false;

		const issueLinksGroupedByRelationship: Relationship[] = useMemo(() => {
			const groupedByRelationship = groupByRelationship(filteredIssueLinks);
			sortRelationshipsAndTheirIssues(groupedByRelationship);
			return groupedByRelationship;
		}, [filteredIssueLinks]);

		if (!issueLinks || issueLinks.length !== filteredIssueLinks.length) {
			return <ErrorCell cellType="issueLinks" reason="Missing required fields" />;
		}

		return (
			<Box>
				<ListCellWithPopup
					items={filteredIssueLinks}
					maxLimit={filteredIssueLinks.length}
					initialIsOpen={initialIsOpen}
					ItemComponent={TagItem}
					PopupContentWrapper={TagItemPopupContentWrapper}
					showMoreTooltip={formatMessage(messages.triggerButton)}
					OverrideContentComponent={
						<ContextualAnalyticsData
							sourceName="issueLinks"
							sourceType={INLINE_DIALOG}
							attributes={{ maxIssueLinksReached }}
						>
							<Box>
								{issueLinksGroupedByRelationship.map((relationship) => (
									<Accordion
										key={relationship.key}
										header={relationship.key}
										length={relationship.issues.length}
										initialIsOpen={accordionInitialIsOpen}
									>
										<TagItemPopupContentWrapper>
											{relationship.issues.map((issue) => (
												<TagItem
													name={issue.name}
													href={issue.href}
													key={issue.key}
													shouldDisplayStrikethrough={issue.shouldDisplayStrikethrough}
												/>
											))}
										</TagItemPopupContentWrapper>
									</Accordion>
								))}
								{maxIssueLinksReached && (
									<RedirectButtonComponent
										issueKey={issueKey}
										issueSearchBaseUrl={issueSearchBaseUrl}
									/>
								)}
							</Box>
							<FireScreenAnalytics />
						</ContextualAnalyticsData>
					}
				/>
			</Box>
		);
	},
);

type RedirectButtonProps = {
	issueKey: string;
	issueSearchBaseUrl: string;
};

const RedirectButtonComponent = ({ issueKey, issueSearchBaseUrl }: RedirectButtonProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<ShowMoreLink
			href={`${issueSearchBaseUrl}?jql=${encodeURIComponent(getLinkJql(issueKey))}`}
			onClick={() => {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'link',
					}),
					'issueLinksSeeAllIssues',
				);
			}}
		>
			{formatMessage(messages.showMoreLink)}
		</ShowMoreLink>
	);
};

type AccordionProps = {
	header: string;
	children: React.ReactNode;
	length: number;
	initialIsOpen?: boolean;
};

const Accordion = memo<AccordionProps>(
	({ header, children, length, initialIsOpen = false }: AccordionProps) => {
		const [isOpen, setIsOpen] = useState(initialIsOpen);
		return (
			<Stack space="space.050">
				<Button
					appearance="subtle"
					spacing="compact"
					iconBefore={
						<Flex
							xcss={xcss({
								transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
								transition: 'transform 0.1s ease-out',
								alignItems: 'center',
							})}
						>
							<ChevronRightIcon label="" />
						</Flex>
					}
					onClick={(e, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'issueLinksAccordion', {
							action: isOpen ? 'closed' : 'opened',
						});
						setIsOpen(!isOpen);
					}}
					// change to xcss when it becomes available for buttons
					// https://atlassian.design/components/button/button-new/examples#custom-buttons
					// eslint-disable-next-line jira/react/no-style-attribute
					style={{ textAlign: 'left' }}
					aria-expanded={isOpen}
				>
					{`${header} (${length})`}
				</Button>
				{isOpen && <Box paddingBlockEnd="space.050">{children}</Box>}
			</Stack>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ShowMoreLink = styled(Link)({
	marginLeft: token('space.200', '16px'),
	marginRight: token('space.200', '16px'),
	lineHeight: token('space.300', '24px'),
	fontWeight: '500',
});
